import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useNavigate } from "react-router-dom";
import { useGlobalContext } from "../context/AuthContext";
import {
  GET_ALL_SUBJECTS,
  UPDATE_SUBJECT,
} from "../../services/graphql/queriesMutations";
import DraggableTreeNode from "./DraggableTreeNode";
import NodeActionModal from "./NodeActionModal";
import {
  persistSubjectTreeStyle,
  readStoredSubjectTreeStyle,
  subjectTreeStyleOptions,
  type SubjectTreeNodeStyle,
} from "./subjectTreeStyle";

import "./tree.css";

interface Subject {
  id: number;
  title: string;
  parentId: number | null;
}

interface TreeSubjectNode extends Subject {
  depth: number;
  childCount: number;
  children: TreeSubjectNode[];
}

const TreeSkeleton: React.FC = () => (
  <div className="subject-tree-loading">
    <div className="subject-tree-loading-title skeleton" />
    <div className="subject-tree-loading-subtitle skeleton" />
    <div className="subject-tree-loading-canvas skeleton" />
  </div>
);

const buildForest = (subjects: Subject[]): TreeSubjectNode[] => {
  const byParent = new Map<number | null, Subject[]>();

  subjects.forEach((subject) => {
    const bucket = byParent.get(subject.parentId) ?? [];
    bucket.push(subject);
    byParent.set(subject.parentId, bucket);
  });

  const buildNode = (subject: Subject, depth: number): TreeSubjectNode => {
    const childSubjects = (byParent.get(subject.id) ?? []).slice().reverse();
    const children = childSubjects.map((child) => buildNode(child, depth + 1));

    return {
      ...subject,
      depth,
      childCount: children.length,
      children,
    };
  };

  return (byParent.get(null) ?? []).slice().reverse().map((subject) => buildNode(subject, 0));
};

const SubjectBranch: React.FC<{
  node: TreeSubjectNode;
  nodeStyle: SubjectTreeNodeStyle;
  onDrop: (draggedId: number, targetId: number) => void;
  onActionClick: (event: React.MouseEvent, node: Subject) => void;
}> = ({ node, nodeStyle, onDrop, onActionClick }) => {
  const hasChildren = node.children.length > 0;
  const branchClassName = [
    "subject-tree-branch",
    `subject-tree-branch-depth-${Math.min(node.depth, 2)}`,
    hasChildren ? "subject-tree-branch-has-children" : "subject-tree-branch-leaf",
  ]
    .filter(Boolean)
    .join(" ");

  const childrenClassName = [
    "subject-tree-branch-children",
    hasChildren ? "subject-tree-branch-children-active" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={branchClassName}>
      <div className="subject-tree-branch-node">
        <DraggableTreeNode
          node={node}
          childCount={node.childCount}
          depth={node.depth}
          nodeStyle={nodeStyle}
          onDrop={onDrop}
          onClick={onActionClick}
        />
      </div>

      {hasChildren && (
        <div className={childrenClassName}>
          {node.children.map((child) => (
            <SubjectBranch
              key={child.id}
              node={child}
              nodeStyle={nodeStyle}
              onDrop={onDrop}
              onActionClick={onActionClick}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const SubjectTree: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useGlobalContext();
  const isAdmin = user?.userType === "admin";
  const { data, loading, error } = useQuery(GET_ALL_SUBJECTS, {
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
  });

  const [updateSubjectParent] = useMutation(UPDATE_SUBJECT, {
    refetchQueries: [{ query: GET_ALL_SUBJECTS }],
    awaitRefetchQueries: true,
  });

  const [selectedNode, setSelectedNode] = useState<Subject | null>(null);
  const [modalAction, setModalAction] = useState<string>("none");
  const [modalVisible, setModalVisible] = useState(false);
  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
  const [nodeStyle, setNodeStyle] = useState<SubjectTreeNodeStyle>(readStoredSubjectTreeStyle);

  useEffect(() => {
    persistSubjectTreeStyle(nodeStyle);
  }, [nodeStyle]);

  const subjects: Subject[] = data?.subjects ?? [];
  const treeRoots = useMemo(() => buildForest(subjects), [subjects]);

  const handleNodeActionClick = (event: React.MouseEvent, node: Subject): void => {
    event.stopPropagation();

    let actionToSet = "none";
    let actualNode = node;

    if (node.title.startsWith("__ACTION_")) {
      const action = node.title.replace("__ACTION_", "").replace("__", "").toLowerCase();
      actionToSet = action;
      actualNode = subjects.find((subject) => subject.id === node.id) ?? node;
    }

    if (actionToSet === "add") {
      setModalAction("add");
    } else if (actionToSet === "update") {
      setModalAction("update");
    } else if (actionToSet === "gist") {
      setModalAction("show");
    } else if (actionToSet === "delete") {
      setModalAction("delete");
    } else {
      setModalAction("none");
    }

    const modalWidth = 320;
    const modalHeight = 220;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let x = event.clientX || viewportWidth / 2;
    let y = event.clientY || viewportHeight / 2;

    if (x + modalWidth > viewportWidth) {
      x = viewportWidth - modalWidth - 20;
    }
    if (y + modalHeight > viewportHeight) {
      y = viewportHeight - modalHeight - 20;
    }

    setSelectedNode(actualNode);
    setModalPosition({ x, y });
    setModalVisible(true);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setSelectedNode(null);
  };

  const handleDrop = async (draggedId: number, targetId: number) => {
    if (!isAdmin) return;
    if (draggedId === targetId) return;
    try {
      await updateSubjectParent({
        variables: { id: draggedId, subject: { parentId: targetId } },
      });
    } catch (mutationError) {
      console.error("Error updating parent subject:", mutationError);
    }
  };

  if (loading && !data) return <TreeSkeleton />;

  if (error) {
    return (
      <div className="subject-tree-feedback subject-tree-feedback-error">
        <p>Error loading subjects: {error.message}</p>
        <p className="subject-tree-feedback-sub">Check the console for details.</p>
      </div>
    );
  }

  if (!subjects.length) {
    return (
      <div className="subject-tree-feedback">
        <p>No subjects found. Create your first subject.</p>
      </div>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div className={`subject-tree-page subject-tree-style-${nodeStyle}`}>
        <div className="subject-tree-shell">
          <div className="subject-tree-header">
            <button
              className="subject-tree-backlink"
              type="button"
              onClick={() => navigate(-1)}
            >
              ← Back
            </button>

            <div className="subject-tree-header-main">
              <div className="subject-tree-heading-wrap">
                <h1 className="subject-tree-title">Knowledge Tree</h1>
                <p className="subject-tree-subtitle">
                  Browse topics and subtopics as a calm left-to-right knowledge map.
                </p>
              </div>

              <div className="subject-tree-style-panel" aria-label="Knowledge tree node style">
                <span className="subject-tree-style-label">Node style</span>
                <div className="subject-tree-style-toggle" role="group" aria-label="Choose subject node style">
                  {subjectTreeStyleOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={[
                        "subject-tree-style-option",
                        nodeStyle === option.value ? "subject-tree-style-option-active" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onClick={() => setNodeStyle(option.value)}
                      aria-pressed={nodeStyle === option.value}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="subject-tree-canvas">
            <div className="subject-tree-map-hint">General → specific</div>

            <div className="subject-tree-root-flow">
              <div className="subject-tree-root-children subject-tree-root-children-active subject-tree-root-children-rootless">
                {treeRoots.map((node) => (
                  <SubjectBranch
                    key={node.id}
                    node={node}
                    nodeStyle={nodeStyle}
                    onDrop={handleDrop}
                    onActionClick={handleNodeActionClick}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        <NodeActionModal
          visible={modalVisible}
          position={modalPosition}
          node={selectedNode}
          onClose={handleModalClose}
          setModalAction={setModalAction}
          modalAction={modalAction}
        />
      </div>
    </DndProvider>
  );
};

export default SubjectTree;
